import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSelector from './LanguageSelector';
import { socialLinks } from '../data/socialLinks';
import { Calendar } from 'lucide-react';
import { restaurantInfo } from '../data/restaurantInfo';

const StunningReservationPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { type: 'success'|'error', message: string }
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '',
    mealType: 'dinner'
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          restaurantEmail: 'ravintolasunsari@gmail.com',
          restaurantPhone: '+358403779200'
        })
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: '🎉 Reservation request sent successfully! We will confirm your booking within 24 hours via email or phone. Thank you for choosing Sunsari!'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          guests: '',
          mealType: 'dinner'
        });
      } else {
        const errorData = await response.json();
        setSubmitStatus({
          type: 'error',
          message: `❌ Failed to send reservation: ${errorData.detail || 'Unknown error'}. Please call us at +358 40 377 9200`
        });
      }
    } catch (error) {
      console.error('Error submitting reservation:', error);
      setSubmitStatus({
        type: 'error',
        message: '❌ Network error. Please call us at +358 40 377 9200 or email ravintolasunsari@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = {
    lunch: ['11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30'],
    dinner: ['11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30']
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-900 to-black">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/20 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex-shrink-0 cursor-pointer group" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_fine-dining-vaasa/artifacts/6i8d0v30_FB_IMG_1759419152155.jpg" 
                alt="Sunsari Restaurant" 
                className="h-12 w-auto max-w-[180px] transform group-hover:scale-110 transition-all duration-500"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              {[
                { name: t('nav.home'), path: '/' },
                { name: t('nav.menu'), path: '/menu?view=alacarte' },
                { name: t('nav.reservations'), path: '/table-booking' },
                { name: t('nav.about'), path: '/about' },
                { name: t('nav.contact'), path: '/contact' }
              ].map((item) => (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  className={`${
                    item.name === 'RESERVATIONS' 
                      ? 'text-amber-300' 
                      : 'text-white/80 hover:text-amber-300'
                  } transition-colors duration-300 font-light tracking-wide text-sm cursor-pointer`}
                >
                  {item.name}
                </button>
              ))}
              
              {/* Language Selector */}
              <LanguageSelector />
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white z-50 relative"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
          
          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-white/10">
              <div className="px-6 py-4 space-y-4">
                {[
                  { name: t('nav.home'), path: '/' },
                  { name: t('nav.menu'), path: '/menu?view=alacarte' },
                  { name: t('nav.reservations'), path: '/table-booking' },
                  { name: t('nav.about'), path: '/about' },
                  { name: t('nav.contact'), path: '/contact' }
                ].map((item) => (
                  <button
                    key={item.name}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left transition-colors duration-300 font-light tracking-wide text-lg py-2 ${
                      item.name === 'RESERVATIONS' 
                        ? 'text-amber-300' 
                        : 'text-white/80 hover:text-amber-300'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* NEW FEATURE BANNER - Interactive Table Booking */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 py-3 shadow-xl animate-pulse">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🪑</span>
              <div>
                <p className="text-white font-bold">NEW! Interactive Table Booking</p>
                <p className="text-white/90 text-sm">Select your exact table on our floor plan!</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/table-booking')}
              className="px-6 py-2 bg-white hover:bg-amber-100 text-amber-600 font-bold rounded-lg transition-all transform hover:scale-105 whitespace-nowrap"
            >
              Try It Now →
            </button>
          </div>
        </div>
      </div>

      {/* Header Section with Background Image */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_multilingual-menu-2/artifacts/qhzt2633_Screenshot_2025-10-02-18-35-21-12_a23b203fd3aafc6dcb84e438dda678b6.jpg"
            alt="Reservation Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-slate-900/90" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="mb-4">
            <Calendar className="w-8 h-8 text-amber-400 mx-auto mb-4" />
            <span className="text-amber-400 text-sm font-light tracking-widest uppercase">{t('reservationPage.reserveTable')}</span>
          </div>
          <h1 className={`text-6xl md:text-7xl font-serif text-white mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {t('reservationPage.makeReservation')}
          </h1>
          <p className="text-white/70 text-xl max-w-3xl mx-auto leading-relaxed">
            {t('reservationPage.description')}
          </p>
        </div>
      </section>

      {/* Reservation Form Section */}
      <section className="pb-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Reservation Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <h3 className="text-2xl font-serif text-white mb-8">{t('reservationPage.reservationDetails')}</h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Meal Type */}
                  <div>
                    <label className="text-white font-medium mb-4 block">{t('reservationPage.mealType')}</label>
                    <div className="flex gap-4">
                      {['lunch', 'dinner'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData({...formData, mealType: type})}
                          className={`px-6 py-3 rounded-full font-medium transition-all duration-300 capitalize ${
                            formData.mealType === type
                              ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-black'
                              : 'bg-slate-700 text-white hover:bg-slate-600'
                          }`}
                        >
                          {type === 'lunch' ? t('reservationPage.lunch') : t('reservationPage.dinner')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Date Selection */}
                  <div>
                    <label className="text-white font-medium mb-2 block">Select Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full bg-slate-700 text-white p-4 rounded-lg border border-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                      required
                    />
                  </div>

                  {/* Time Selection */}
                  <div>
                    <label className="text-white font-medium mb-4 block">Available Times for {formData.mealType}</label>
                    <div className="grid grid-cols-4 gap-3">
                      {timeSlots[formData.mealType].map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setFormData({...formData, time})}
                          className={`p-3 rounded-lg text-center transition-all duration-300 ${
                            formData.time === time
                              ? 'bg-teal-500 text-white'
                              : 'bg-slate-700 text-white/70 hover:bg-slate-600'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Guest Count */}
                  <div>
                    <label className="text-white font-medium mb-2 block">Number of Guests</label>
                    <select
                      value={formData.guests}
                      onChange={(e) => setFormData({...formData, guests: e.target.value})}
                      className="w-full bg-slate-700 text-white p-4 rounded-lg border border-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                      required
                    >
                      <option value="">Select guests</option>
                      {[...Array(20)].map((_, i) => {
                        const num = i + 1;
                        return <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                      })}
                      <option value="20+">20+ Guests (Call us)</option>
                    </select>
                  </div>

                  {/* Personal Information */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white font-medium mb-2 block">Full Name</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-slate-700 text-white p-4 rounded-lg border border-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-white font-medium mb-2 block">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-slate-700 text-white p-4 rounded-lg border border-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-white font-medium mb-2 block">Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-slate-700 text-white p-4 rounded-lg border border-slate-600 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                      placeholder="+358 40 123 4567"
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-4 text-lg font-semibold rounded-lg transition-all duration-300 ${
                      isSubmitting 
                        ? 'bg-gray-500 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 hover:scale-105'
                    } text-white`}
                  >
                    {isSubmitting ? 'SENDING REQUEST...' : 'CONFIRM RESERVATION'}
                  </Button>

                  {/* Status Message */}
                  {submitStatus && (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      submitStatus.type === 'success' 
                        ? 'bg-green-900/50 border-green-500/50 text-green-200' 
                        : 'bg-red-900/50 border-red-500/50 text-red-200'
                    }`}>
                      <p className="text-center font-medium">{submitStatus.message}</p>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Restaurant Information Sidebar */}
            <div className="space-y-8">
              {/* Restaurant Info */}
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                  Address
                </h4>
                <div className="space-y-1 text-white/70">
                  <p>{restaurantInfo.address.street}</p>
                  <p>{restaurantInfo.address.postalCode} {restaurantInfo.address.city}</p>
                  <p>{restaurantInfo.address.country}</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <div className="w-2 h-2 bg-teal-400 rounded-full mr-3"></div>
                  Phone
                </h4>
                <p className="text-white/70">{restaurantInfo.contact.phone}</p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h4 className="text-white font-semibold mb-4 flex items-center">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mr-3"></div>
                  Hours
                </h4>
                <div className="space-y-1 text-white/70 text-sm">
                  <p>Monday: {restaurantInfo.openingHours.monday.display}</p>
                  <p>Tue-Thu: {restaurantInfo.openingHours.tuesday.display}</p>
                  <p>Friday: {restaurantInfo.openingHours.friday.display}</p>
                  <p>Saturday: {restaurantInfo.openingHours.saturday.display}</p>
                  <p>Sunday: {restaurantInfo.openingHours.sunday.display}</p>
                </div>
              </div>

              {/* Reservation policies removed as requested */}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-black/50 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <h3 className="text-2xl font-serif text-amber-300 mb-4">SUNSARI</h3>
              <p className="text-white/60 leading-relaxed">
                7+ years of excellence as one of Vaasa's finest and most popular restaurants. Authentic Nepalese and Indian flavours.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-white/60 text-sm">
                <p>{restaurantInfo.address.street}</p>
                <p>{restaurantInfo.address.postalCode} {restaurantInfo.address.city}, {restaurantInfo.address.country}</p>
                <p>{restaurantInfo.contact.phone}</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Hours</h4>
              <div className="space-y-1 text-white/60 text-sm">
                <p>Mon: 11:00-15:00</p>
                <p>Tue-Thu: 11:00-21:00</p>
                <p>Fri: 11:00-22:00</p>
                <p>Sat: 12:00-22:00</p>
                <p>Sun: 12:00-20:00</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                {['Instagram', 'Facebook', 'Twitter'].map((social) => (
                  <a
                    key={social}
                    href="#"
                    className="text-white/60 hover:text-amber-300 transition-colors duration-300"
                  >
                    {social}
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/40">
            <p>&copy; 2024 Sunsari Restaurant. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StunningReservationPage;